import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../ui/dialog";
import { ExternalLink, Save, Plus, X, Sparkles, Search, Loader2, Globe2, Newspaper, TrendingUp } from "lucide-react";
import { apiFetch } from "../../../../utils/api";
import { EditorialUpdates } from "./EditorialUpdates";
import { toast } from "sonner";

const blank = { name: "", rationale: "", keywords: "", focus_country: "", countries: [], source_urls: "" };
const toForm = v => ({ name: v.name, rationale: v.rationale || "", keywords: (v.keywords || []).join(", "), focus_country: v.focus_country || "", countries: v.countries || [], source_urls: (v.source_urls || []).join("\n") });
const label  = v => ({ user: "User Suggested", editor: "Editor's Choice", system: "System Suggested", ai: "AI Suggested" }[v] || "System Suggested");

/* ── helpers ───────────────────────────────────────────── */
const timeAgo = iso => {
  if (!iso) return "";
  const h = (Date.now() - new Date(iso)) / 3600000;
  if (h < 1)  return `${Math.round(h * 60)}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

/* ── Intelligence panel (new-topic only) ───────────────── */
const IntelligencePanel = ({ topicName, preview, searching }) => {
  if (!topicName || topicName.length < 3) return (
    <div className="flex flex-col items-center justify-center h-full py-16 text-center">
      <Globe2 size={32} className="text-[var(--muted)] mb-3" />
      <p className="text-[13px] text-[var(--muted)]">Start typing a topic to see live coverage from your database</p>
    </div>
  );

  if (searching) return (
    <div className="flex flex-col items-center justify-center h-full py-16">
      <Loader2 size={24} className="animate-spin text-[var(--muted)] mb-3" />
      <p className="text-[12px] text-[var(--muted)]">Searching coverage…</p>
    </div>
  );

  if (!preview) return null;

  const { total, stories = [], countries = [], sources = [] } = preview;

  return (
    <div className="flex flex-col gap-5 h-full overflow-y-auto pr-1" data-testid="intelligence-panel">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Newspaper,  label: "Stories",   value: total },
          { icon: Globe2,     label: "Countries", value: countries.length },
          { icon: TrendingUp, label: "Sources",   value: sources.length },
        ].map(({ icon: Icon, label: lbl, value }) => (
          <div key={lbl} className="rounded-xl border border-[var(--hairline)] p-3 text-center bg-[var(--surface-2)]">
            <Icon size={14} className="text-[var(--crimson)] mx-auto mb-1" />
            <p className="font-serif text-xl leading-none">{value}</p>
            <p className="text-[10px] text-[var(--muted)] mt-0.5">{lbl}</p>
          </div>
        ))}
      </div>

      {total === 0 && (
        <p className="text-[12px] text-[var(--muted)] text-center py-4">
          No stories found in the last 72 h for this topic name. Try different keywords.
        </p>
      )}

      {/* Country breakdown */}
      {countries.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--muted)] mb-2">Coverage by country</p>
          <div className="space-y-1.5">
            {countries.slice(0, 8).map(c => (
              <div key={c.slug} className="flex items-center gap-2" data-testid={`intel-country-${c.slug}`}>
                <span className="text-base w-5 text-center shrink-0">{c.flag}</span>
                <span className="text-[12px] text-[var(--ink-2)] flex-1 truncate">{c.name}</span>
                <div className="flex items-center gap-1.5">
                  <div
                    className="h-1.5 rounded-full bg-[var(--crimson)] opacity-60"
                    style={{ width: `${Math.max(8, (c.count / countries[0].count) * 60)}px` }}
                  />
                  <span className="text-[11px] text-[var(--muted)] w-6 text-right">{c.count}</span>
                </div>
              </div>
            ))}
            {countries.length > 8 && (
              <p className="text-[11px] text-[var(--muted)] pl-7">+{countries.length - 8} more countries</p>
            )}
          </div>
        </div>
      )}

      {/* Top headlines */}
      {stories.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--muted)] mb-2">Top stories · last 72h</p>
          <div className="space-y-3">
            {stories.map((s, i) => (
              <div key={s.id || i} className="border-l-2 border-[var(--hairline)] pl-3 group" data-testid={`intel-story-${i}`}>
                <p className="text-[12px] font-medium text-[var(--ink)] leading-snug line-clamp-2">
                  {s.headline}
                </p>
                <p className="text-[10.5px] text-[var(--muted)] mt-0.5">
                  {s.source} · {timeAgo(s.published_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main TopicEditor component ────────────────────────── */
export const TopicEditor = ({ selection, countries, onClose, onChanged }) => {
  /* shared state */
  const [topic,  setTopic]  = useState(null);
  const [form,   setForm]   = useState(blank);
  const [error,  setError]  = useState("");
  const [busy,   setBusy]   = useState(false);
  const [action, setAction] = useState(null);
  const [ack,    setAck]    = useState(false);
  const [prepare, setPrepare] = useState([]);
  const [extraCountry, setExtraCountry] = useState("");
  const [actionRevision, setActionRevision] = useState(0);
  const [filling, setFilling]  = useState(false);

  /* new-topic only */
  const [preview,   setPreview]   = useState(null);
  const [searching, setSearching] = useState(false);

  /* isNew: true only when no selection.id AND no saved topic yet */
  const isNew = !selection.id && !topic;

  const confirmationRef = useRef(null);
  const errorRef        = useRef(null);
  const actionTriggerRef = useRef(null);

  /* scroll helpers */
  useEffect(() => {
    if (!action) return;
    const id = requestAnimationFrame(() => { confirmationRef.current?.focus({ preventScroll: true }); confirmationRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" }); });
    return () => cancelAnimationFrame(id);
  }, [action, actionRevision]);
  useEffect(() => {
    if (!error) return;
    const id = requestAnimationFrame(() => { errorRef.current?.focus({ preventScroll: true }); errorRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" }); });
    return () => cancelAnimationFrame(id);
  }, [error]);

  /* load existing topic */
  const load = v => { setTopic(v); setForm(toForm(v)); };
  useEffect(() => {
    if (!selection.id) return;
    setBusy(true);
    apiFetch(`/api/admin/perspectives/topics/${selection.id}`).then(v => load(v.data)).catch(() => setError("Topic details could not be loaded.")).finally(() => setBusy(false));
  }, [selection.id]);

  const frozen = !!topic?.accepted_at;
  const reader = selection.reader;
  const dirty  = topic && JSON.stringify(form) !== JSON.stringify(toForm(topic));
  const field  = (k, v) => setForm(o => ({ ...o, [k]: v }));
  const countryName = slug => countries.find(c => c.slug === slug)?.name || slug;
  const toggleCountry = slug => field("countries", form.countries.includes(slug) ? form.countries.filter(c => c !== slug) : [...form.countries, slug]);

  /* live intelligence search (new topics, debounced) */
  useEffect(() => {
    if (!isNew || !form.name || form.name.length < 3) { setPreview(null); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch("/api/admin/perspectives/preview-search", { method: "POST", body: JSON.stringify({ topic: form.name }) });
        setPreview(res);
      } catch { setPreview(null); }
      finally { setSearching(false); }
    }, 500);
    return () => { clearTimeout(timer); setSearching(false); };
  }, [isNew, form.name]);

  /* AI fill — new topic (uses live preview results) */
  const autoFillNew = async () => {
    setFilling(true);
    try {
      const res = await apiFetch("/api/admin/perspectives/autofill-new", { method: "POST", body: JSON.stringify({ topic: form.name, stories: preview?.stories || [] }) });
      if (res?.rationale) field("rationale", res.rationale);
      if (res?.keywords)  field("keywords",  res.keywords);
      toast.success("AI brief generated — review and save when ready");
    } catch { toast.error("Auto-fill failed. Try again."); }
    finally { setFilling(false); }
  };

  /* AI fill — existing topic (uses stored evidence) */
  const autoFillExisting = async () => {
    if (!topic?.id) return;
    setFilling(true);
    try {
      const res = await apiFetch(`/api/admin/perspectives/topics/${topic.id}/autofill`, { method: "POST" });
      if (res?.data?.rationale) field("rationale", res.data.rationale);
      if (res?.data?.keywords)  field("keywords",  res.data.keywords);
      toast.success("Fields filled — review and save when ready");
    } catch { toast.error("Auto-fill failed. Try again."); }
    finally { setFilling(false); }
  };

  /* save */
  const save = async () => {
    if (!form.name.trim()) { setError("Topic name is required."); return; }
    if (!form.rationale || form.rationale.trim().length < 12) { setError("Please fill in 'Why this matters' (at least 12 characters) before saving. Use the AI Fill button to generate it."); return; }
    setBusy(true); setError("");
    try {
      const payload = { ...form, keywords: form.keywords.split(",").map(v => v.trim()).filter(Boolean), focus_country: form.focus_country || null, source_urls: form.source_urls.split("\n").map(v => v.trim()).filter(Boolean) };
      const value = await apiFetch(topic ? `/api/admin/perspectives/topics/${topic.id}` : "/api/admin/perspectives", { method: topic ? "PUT" : "POST", body: JSON.stringify(payload) });
      load(value.data); onChanged();
    } catch (err) { setError(typeof err.data?.detail === "string" ? err.data.detail : "Check the title, rationale, countries and source URLs."); }
    finally { setBusy(false); }
  };

  /* action */
  const chooseAction = v => { actionTriggerRef.current = document.activeElement; setAction(v); setActionRevision(n => n + 1); setAck(false); setPrepare([]); setError(""); };
  const cancelAction = () => { setAction(null); setError(""); requestAnimationFrame(() => { actionTriggerRef.current?.focus({ preventScroll: true }); actionTriggerRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" }); }); };
  const perform = async () => {
    setBusy(true); setError("");
    try {
      if (reader) await apiFetch(`/api/admin/perspectives/readers/${reader.id}/decision`, { method: "POST", body: JSON.stringify({ action, confirm: true }) });
      else {
        if (action === "publish" && dirty) { setError("Save your changes and check the evidence before publishing."); return; }
        await apiFetch(`/api/admin/perspectives/topics/${topic.id}/action`, { method: "POST", body: JSON.stringify({ action, confirm: true, acknowledge_gaps: ack, generate_countries: prepare, confirm_generation: prepare.length > 0 }) });
      }
      onChanged(); onClose();
    } catch (err) { setError(typeof err.data?.detail === "string" ? err.data.detail : "The action could not be completed."); }
    finally { setBusy(false); }
  };

  /* ── render ─────────────────────────────────────────── */
  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <DialogContent
        className={`${isNew ? "max-w-5xl" : "max-w-4xl"} w-[calc(100%-24px)] max-h-[90dvh] overflow-y-auto bg-[var(--paper)]`}
        data-testid="editorial-topic-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl" data-testid="editorial-dialog-title">
            {reader ? reader.title : topic ? topic.name : "New perspective topic"}
          </DialogTitle>
          <DialogDescription>
            {reader ? "Reader proposal · private editorial review" : isNew ? "Type a topic to see live coverage intelligence, then generate an AI brief in one click" : "Editorial evidence · no automatic AI preparation"}
          </DialogDescription>
        </DialogHeader>

        {error && !action && <p ref={errorRef} tabIndex={-1} role="alert" className="text-sm text-red-700" data-testid="editorial-dialog-error">{error}</p>}

        {/* ── Reader proposal flow (unchanged) ── */}
        {reader ? (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" data-testid="editorial-reader-reason">{reader.reason}</p>
            <p className="text-xs" data-testid="editorial-reader-email">Submitter email (admin only): {reader.submitter_email}</p>
            <p className="text-xs" data-testid="editorial-reader-likes">{reader.likes} likes · {reader.environment === "preview" ? "Preview test votes do not qualify for production" : "1,000 required before editorial publication"}</p>
            {reader.reference_url && <a href={reader.reference_url} rel="noopener noreferrer" target="_blank" className="text-xs underline break-all" data-testid="editorial-reader-reference">Reader-supplied reference (not verified)</a>}
            <div className="flex flex-wrap gap-3">
              <button type="button" disabled={!reader.eligible || busy} onClick={() => chooseAction("draft")} className="border rounded-lg px-4 min-h-10 disabled:opacity-40" data-testid="editorial-reader-draft">Move to editorial draft</button>
              <button type="button" onClick={() => chooseAction("dismiss")} className="border rounded-lg px-4 min-h-10 text-red-700" data-testid="editorial-reader-dismiss">Delete unwanted proposal</button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs text-[var(--muted)]" data-testid="editorial-dialog-origin">{label(topic?.origin || "editor")} · {topic?.editorial_status || "New draft"}</p>

            {/* ══════════════════════════════════════════════
                NEW TOPIC — Two-panel intelligence layout
                ══════════════════════════════════════════════ */}
            {isNew ? (
              <div className="flex flex-col gap-5">
                {/* Topic name — full width, prominent */}
                <div>
                  <label className="text-xs font-medium text-[var(--ink)]">Topic name</label>
                  <div className="relative mt-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                    <input
                      autoFocus
                      value={form.name}
                      onChange={e => field("name", e.target.value)}
                      maxLength={200}
                      placeholder="e.g. West Asia conflict and its international impact"
                      className="block w-full border rounded-xl bg-transparent pl-9 pr-4 py-3 text-[15px] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--crimson)]/30 transition"
                      data-testid="editorial-name"
                    />
                    {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--muted)]" />}
                  </div>
                </div>

                {/* Two-column panel */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* LEFT — Intelligence panel */}
                  <div className="border border-[var(--hairline)] rounded-2xl p-5 bg-[var(--surface-2)] min-h-[320px]" data-testid="intel-panel-wrap">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--muted)] mb-4">Live coverage intelligence · 72h</p>
                    <IntelligencePanel topicName={form.name} preview={preview} searching={searching} />
                  </div>

                  {/* RIGHT — Editorial brief form */}
                  <div className="flex flex-col gap-4">
                    {/* AI Generate button */}
                    <button
                      type="button"
                      onClick={autoFillNew}
                      disabled={filling || busy || !preview?.total}
                      data-testid="editorial-autofill-new"
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[var(--hairline)] text-[13px] font-medium text-[var(--muted)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] disabled:opacity-40 transition-colors"
                    >
                      <Sparkles size={15} className={filling ? "animate-pulse text-[var(--crimson)]" : ""} />
                      {filling ? "Generating AI brief…" : !preview?.total ? "Type a topic to generate brief" : `Generate AI brief from ${preview.total} stories`}
                    </button>

                    <label className="text-xs font-medium">
                      Why this matters
                      <textarea
                        value={form.rationale}
                        onChange={e => field("rationale", e.target.value)}
                        maxLength={1600}
                        rows={4}
                        placeholder="AI will fill this — or write your own"
                        className="block w-full border rounded-xl bg-transparent p-3 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--crimson)]/20 transition resize-none"
                        data-testid="editorial-rationale"
                      />
                    </label>

                    <label className="text-xs font-medium">
                      Related search phrases, comma-separated
                      <input
                        value={form.keywords}
                        onChange={e => field("keywords", e.target.value)}
                        placeholder="AI will fill this — or write your own"
                        className="block w-full border rounded-xl bg-transparent p-3 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--crimson)]/20 transition"
                        data-testid="editorial-keywords"
                      />
                    </label>

                    <label className="text-xs font-medium">
                      Domestic country focus
                      <select
                        value={form.focus_country}
                        onChange={e => field("focus_country", e.target.value)}
                        className="block w-full border rounded-xl bg-[var(--paper)] p-3 mt-1 text-sm"
                        data-testid="editorial-focus"
                      >
                        <option value="">Not established</option>
                        {countries.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                      </select>
                    </label>

                    <label className="text-xs font-medium">
                      Additional source URLs (optional, one per line)
                      <textarea
                        rows={2}
                        value={form.source_urls}
                        onChange={e => field("source_urls", e.target.value)}
                        className="block w-full border rounded-xl bg-transparent p-3 mt-1 text-sm resize-none"
                        data-testid="editorial-source-urls"
                      />
                    </label>
                  </div>
                </div>

                {/* Save button */}
                <div className="flex flex-col gap-2 border-t pt-4">
                  {error && <p role="alert" className="text-sm text-red-700 rounded-lg bg-red-50 px-3 py-2" data-testid="editorial-dialog-error-new">{error}</p>}
                  <div className="flex gap-3">
                    <button type="button" onClick={save} disabled={busy || !form.name.trim()} className="border rounded-xl px-5 min-h-11 inline-flex items-center gap-2 disabled:opacity-40" data-testid="editorial-save">
                      <Save size={15} />{busy ? "Saving…" : "Save & check evidence"}
                    </button>
                  </div>
                </div>
              </div>

            ) : (
              /* ══════════════════════════════════════════════
                 EXISTING TOPIC — standard edit form
                 ══════════════════════════════════════════════ */
              <>
                <label className="text-xs">Topic
                  <input value={form.name} disabled={frozen} onChange={e => field("name", e.target.value)} maxLength={200} className="block w-full border rounded-lg bg-transparent p-3 mt-1 text-sm disabled:opacity-70" data-testid="editorial-name" />
                </label>

                {!frozen && (
                  <button type="button" onClick={autoFillExisting} disabled={filling || busy} data-testid="editorial-autofill"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--hairline)] text-[12px] text-[var(--muted)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] disabled:opacity-40 transition-colors self-start">
                    <Sparkles size={13} className={filling ? "animate-pulse text-[var(--crimson)]" : ""} />
                    {filling ? "Generating…" : "AI Fill rationale & keywords"}
                  </button>
                )}

                <label className="text-xs">Why this matters
                  <textarea value={form.rationale} disabled={frozen} onChange={e => field("rationale", e.target.value)} maxLength={1600} rows={3} className="block w-full border rounded-lg bg-transparent p-3 mt-1 text-sm disabled:opacity-70" data-testid="editorial-rationale" />
                </label>

                {!frozen && (
                  <>
                    <label className="text-xs">Related search phrases, comma-separated
                      <input value={form.keywords} onChange={e => field("keywords", e.target.value)} className="block w-full border rounded-lg bg-transparent p-3 mt-1 text-sm" data-testid="editorial-keywords" />
                    </label>
                    <label className="text-xs">Additional source URLs (optional, one per line)
                      <textarea rows={2} value={form.source_urls} onChange={e => field("source_urls", e.target.value)} aria-describedby="editorial-source-help" className="block w-full border rounded-lg bg-transparent p-3 mt-1 text-sm" data-testid="editorial-source-urls" />
                      <span id="editorial-source-help" className="block mt-2 text-[var(--muted)]" data-testid="editorial-source-help">{topic?.evidence?.length ? `${topic.evidence.length} existing reports are already linked. Additional URLs are not required.` : "Optional references; existing suggestion evidence is retained without adding URLs."}</span>
                    </label>
                  </>
                )}

                <label className="text-xs">Domestic country focus
                  <select disabled={frozen} value={form.focus_country} onChange={e => field("focus_country", e.target.value)} className="block max-w-full w-full border rounded-lg bg-[var(--paper)] p-3 mt-1 text-sm" data-testid="editorial-focus">
                    <option value="">Not established</option>
                    {countries.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </label>

                <div className="border-y border-[var(--hairline)] py-4">
                  <h3 className="text-sm font-medium mb-3">Evidence-backed country recommendations</h3>
                  {!topic?.recommendations?.length && <p className="text-xs text-[var(--muted)]" data-testid="editorial-no-countries">Save the draft to check existing reporting. No country involvement is inferred from model memory.</p>}
                  <div className="flex flex-wrap gap-2">
                    {topic?.recommendations?.map(c => (
                      <button type="button" key={c.slug} disabled={frozen} onClick={() => toggleCountry(c.slug)} title={c.reasons.join(" · ")} aria-pressed={form.countries.includes(c.slug)}
                        className={`text-xs text-left border rounded-lg p-3 max-w-full ${form.countries.includes(c.slug) ? "border-[var(--crimson)] bg-[var(--surface-2)]" : "border-[var(--hairline)]"}`}
                        data-testid={`editorial-country-${c.slug}`}>
                        <span className="font-medium">{c.name}</span>
                        <span className="block text-[10px] mt-1 text-[var(--muted)]">{c.mentions} explicit report mentions · {c.official_sources} official-source organisations</span>
                      </button>
                    ))}
                  </div>
                  {!frozen && (
                    <div className="flex gap-2 mt-4">
                      <select value={extraCountry} onChange={e => setExtraCountry(e.target.value)} className="flex-1 min-w-0 max-w-full border bg-[var(--paper)] rounded-lg p-2 text-xs" aria-label="Additional country" data-testid="editorial-add-country-select">
                        <option value="">Add another country…</option>
                        {countries.filter(c => !form.countries.includes(c.slug)).map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                      </select>
                      <button type="button" disabled={!extraCountry} onClick={() => { toggleCountry(extraCountry); setExtraCountry(""); }} aria-label="Add selected country" className="border rounded-lg p-3 disabled:opacity-30" data-testid="editorial-add-country"><Plus size={16} /></button>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="editorial-selected-countries">
                    {form.countries.map(c => (
                      <span key={c} className="inline-flex gap-2 items-center text-xs bg-[var(--surface-2)] rounded px-2 py-1">
                        {countryName(c)}
                        {!frozen && <button type="button" aria-label={`Remove ${countryName(c)}`} onClick={() => toggleCountry(c)} data-testid={`editorial-remove-${c}`}><X size={13} /></button>}
                      </span>
                    ))}
                  </div>
                </div>

                {topic && (
                  <>
                    <p className="text-xs text-[var(--muted)]" data-testid="editorial-saved-counts">Saved evidence: {topic.article_count || 0} reports · {topic.publisher_count || 0} organisations · {topic.publications?.domestic || 0} domestic / {topic.publications?.international || 0} international for {countryName(topic.publications?.focus_country) || "unselected focus"}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {Object.entries(topic.source_group_counts || {}).map(([group, count]) => (
                        <p key={group} className="text-xs capitalize border-t pt-2" data-testid={`editorial-detail-group-${group}`}>{group} <strong>{count}</strong> / 3</p>
                      ))}
                    </div>
                    <ul className="text-xs text-[var(--muted)] space-y-1" data-testid="editorial-coverage-gaps">
                      {topic.coverage_gaps?.map((gap, i) => <li key={gap} data-testid={`editorial-gap-${i}`}>• {gap}</li>)}
                    </ul>
                    {!!topic.unverified_links?.length && <p className="text-xs text-amber-800" data-testid="editorial-unverified-links">{topic.unverified_links.length} pasted links have not been matched to eligible stored reporting and are not counted.</p>}
                    <details>
                      <summary className="cursor-pointer text-sm min-h-10 py-2" data-testid="editorial-evidence-toggle">Sources & evidence ({topic.evidence?.length || 0})</summary>
                      <div className="max-h-64 overflow-y-auto divide-y">
                        {topic.evidence?.map((r, i) => (
                          <div key={r.id} className="py-3" data-testid={`editorial-source-${i}`}>
                            <p className="text-[10px] text-[var(--muted)]">{r.publisher} · {r.source_group} · {r.published_at?.slice(0, 10)}</p>
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--crimson)] inline-flex gap-2 mt-1" data-testid={`editorial-source-link-${i}`}>
                              <span className="break-words min-w-0">{r.title}</span><ExternalLink size={12} className="shrink-0" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </details>
                  </>
                )}

                <div className="flex flex-wrap gap-3 border-t pt-4">
                  {!frozen && <button type="button" onClick={save} disabled={busy} className="border rounded-lg px-4 min-h-11 inline-flex items-center gap-2" data-testid="editorial-save"><Save size={15} />{busy ? "Saving…" : "Save & check evidence"}</button>}
                  {topic && (
                    <>
                      {topic.editorial_status !== "published" && topic.editorial_status !== "suggestion" && topic.editorial_status !== "deleted" && <button type="button" onClick={() => chooseAction("publish")} className="bg-[var(--crimson)] text-white rounded-lg px-4 min-h-11" data-testid="editorial-publish">Publish</button>}
                      {topic.editorial_status === "published" && <button type="button" onClick={() => chooseAction("archive")} className="border rounded-lg px-4 min-h-11" data-testid="editorial-archive">Archive</button>}
                      {topic.editorial_status === "deleted" ? <button onClick={() => chooseAction("restore")} className="border rounded-lg px-4 min-h-11" data-testid="editorial-restore">Restore to archive</button> : <button type="button" onClick={() => chooseAction("delete")} className="border border-red-200 text-red-700 rounded-lg px-4 min-h-11" data-testid="editorial-delete">Delete</button>}
                    </>
                  )}
                </div>
                {topic?.editorial_status === "published" && <EditorialUpdates topic={topic} countries={countries} />}
              </>
            )}
          </>
        )}

        {/* ── Confirmation panel (unchanged) ── */}
        {action && (
          <section ref={confirmationRef} tabIndex={-1} className="border-t border-[var(--hairline)] pt-5 space-y-4 focus:outline-none" role="region" aria-labelledby="editorial-confirmation-heading" data-testid="editorial-confirmation">
            <h3 id="editorial-confirmation-heading" className="text-sm font-medium" data-testid="editorial-confirmation-heading">Confirm {action}</h3>
            <p className="text-sm" data-testid="editorial-action-summary">{action === "publish" ? "Publish as Editor's Choice, with visible source gaps. Ten current topics maximum." : action === "delete" ? frozen ? "Remove this published topic from public access. Its historical analysis is retained for restoration. Archive instead for public study access." : "Delete this unpublished payload and suppress this suggestion from future refreshes." : action === "archive" ? "Remove from the current selection; keep it publicly available in the archive." : `Confirm ${action}?`}</p>
            {action === "publish" && (
              <>
                <label className="flex gap-3 items-start text-xs"><input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} data-testid="editorial-ack-gaps" /><span>I have reviewed the evidence and acknowledge the visible gaps.</span></label>
                <p className="text-xs">Optional initial country analysis — paid only if a saved briefing does not exist:</p>
                <div className="flex flex-wrap gap-4">{form.countries.map(c => <label key={c} className="inline-flex gap-2 items-center text-xs"><input type="checkbox" checked={prepare.includes(c)} onChange={() => setPrepare(old => old.includes(c) ? old.filter(v => v !== c) : [...old, c])} data-testid={`editorial-prepare-${c}`} />{countryName(c)}</label>)}</div>
                <p className="text-xs font-medium" data-testid="editorial-generation-count">{prepare.length} selected preparation requests. No countries are generated automatically.</p>
              </>
            )}
            {error && <p ref={errorRef} tabIndex={-1} role="alert" className="text-sm text-red-700" data-testid="editorial-dialog-error">{error}</p>}
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={perform} disabled={busy || (action === "publish" && !ack)} className="bg-[var(--crimson)] text-white rounded-lg px-4 min-h-11 disabled:opacity-40" data-testid="editorial-confirm-action">{busy ? "Working…" : `Confirm ${action}`}</button>
              <button type="button" disabled={busy} onClick={cancelAction} className="border rounded-lg px-4 min-h-11 disabled:opacity-40" data-testid="editorial-cancel-action">Cancel</button>
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
};
